import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const tok = () => localStorage.getItem('token');
const hdrs = () => ({ Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json' });
const gh = (n) => `GHS ${Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })}`;

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', lavender: '#A78BFA', sky: '#38BDF8',
  text: '#E8EDF5', sub: '#C5CDD8',
};

const TAB_KEYS   = ['overview', 'loyalty', 'segments', 'sms', 'birthdays'];
const TAB_LABELS = ['Overview', 'Loyalty Points', 'Segments', 'Bulk SMS', 'Birthdays'];

const SEGMENT_META = {
  vip:      { label: 'VIP',      bg: 'rgba(240,180,41,0.12)',  color: '#F0B429', border: 'rgba(240,180,41,0.3)',  icon: '⭐', desc: 'GHS 2,000+ spent OR 500+ points' },
  regular:  { label: 'Regular',  bg: 'rgba(56,189,248,0.1)',   color: '#38BDF8', border: 'rgba(56,189,248,0.25)', icon: '🥈', desc: 'Active, bought in last 60 days'    },
  atRisk:   { label: 'At Risk',  bg: 'rgba(255,107,107,0.1)',  color: '#FF6B6B', border: 'rgba(255,107,107,0.3)', icon: '⚠️', desc: 'No purchase in 60–90 days'        },
  inactive: { label: 'Inactive', bg: 'rgba(136,146,164,0.08)', color: '#8892A4', border: 'rgba(136,146,164,0.2)', icon: '😴', desc: 'No purchase in 90+ days'           },
  new:      { label: 'New',      bg: 'rgba(0,201,177,0.1)',    color: '#00C9B1', border: 'rgba(0,201,177,0.25)',  icon: '🌱', desc: 'First purchase or no purchases yet' },
};

const STYLES = `
  .crm-page { padding: 28px 32px; font-family: 'Cabinet Grotesk', sans-serif; max-width: 1200px; margin: 0 auto; }
  .crm-heading { margin: 0 0 6px; font-size: 1.55rem; font-weight: 800; color: ${C.text}; letter-spacing: -0.3px; }
  .crm-sub { margin: 0 0 24px; font-size: 0.875rem; color: ${C.muted}; }
  .crm-tabs { display: flex; gap: 3px; background: rgba(255,255,255,0.04); border-radius: 12px; padding: 4px; margin-bottom: 24px; width: fit-content; flex-wrap: wrap; border: 1px solid ${C.border}; }
  .crm-tab { padding: 8px 18px; border-radius: 9px; font-size: 0.82rem; font-weight: 600; cursor: pointer; border: none; transition: all 0.15s; background: transparent; color: ${C.muted}; font-family: inherit; }
  .crm-tab.active { background: ${C.gold}; color: ${C.ink}; font-weight: 700; }
  .crm-tab:hover:not(.active) { color: ${C.text}; background: rgba(255,255,255,0.05); }
  .crm-panel { background: ${C.surface}; border-radius: 16px; border: 1px solid ${C.border}; padding: 24px; }
  .crm-kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 12px; margin-bottom: 24px; }
  .crm-kpi { background: rgba(255,255,255,0.04); border: 1px solid ${C.border}; border-radius: 12px; padding: 16px 18px; display: flex; align-items: center; gap: 14px; }
  .crm-kpi-icon { width: 42px; height: 42px; border-radius: 10px; font-size: 18px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .crm-kpi-val { font-size: 22px; font-weight: 800; line-height: 1; font-family: 'JetBrains Mono', monospace; }
  .crm-kpi-label { font-size: 11px; color: ${C.muted}; margin-top: 4px; }
  .crm-filter-row { display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
  .crm-search { flex: 1; min-width: 200px; padding: 9px 14px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 8px; font-size: 13px; font-family: inherit; outline: none; color: ${C.text}; }
  .crm-search:focus { border-color: ${C.teal}; }
  .crm-search::placeholder { color: ${C.muted}; }
  .crm-select { padding: 9px 14px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 8px; font-size: 13px; font-family: inherit; outline: none; color: ${C.text}; }
  .crm-select:focus { border-color: ${C.teal}; }
  .crm-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .crm-table thead tr { background: rgba(255,255,255,0.03); }
  .crm-table thead th { padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 700; color: ${C.muted}; border-bottom: 1px solid ${C.border}; white-space: nowrap; letter-spacing: 0.05em; text-transform: uppercase; }
  .crm-table tbody tr { border-bottom: 1px solid ${C.border}; transition: background 150ms; }
  .crm-table tbody tr:last-child { border-bottom: none; }
  .crm-table tbody tr:hover { background: rgba(255,255,255,0.025); }
  .crm-td { padding: 10px 14px; color: ${C.text}; vertical-align: middle; }
  .crm-card { background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 12px; padding: 20px; }
  .crm-card-title { margin: 0 0 16px; font-size: 14px; font-weight: 700; color: ${C.text}; }
  .crm-btn-primary { padding: 9px 18px; background: ${C.gold}; color: ${C.ink}; border: none; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; transition: opacity 150ms; white-space: nowrap; }
  .crm-btn-primary:hover { opacity: 0.88; }
  .crm-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .crm-btn-outline { padding: 8px 16px; background: rgba(255,255,255,0.05); color: ${C.sub}; border: 1px solid ${C.border}; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 150ms; }
  .crm-btn-outline:hover { color: ${C.text}; background: rgba(255,255,255,0.09); }
  .crm-input { width: 100%; padding: 9px 12px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 8px; font-size: 13px; font-family: inherit; outline: none; box-sizing: border-box; color: ${C.text}; }
  .crm-input:focus { border-color: ${C.teal}; }
  .crm-input::placeholder { color: ${C.muted}; }
  .crm-toast { background: rgba(0,201,177,0.12); border: 1.5px solid rgba(0,201,177,0.3); border-radius: 10px; padding: 12px 18px; margin-bottom: 16px; color: ${C.teal}; font-weight: 600; font-size: 13px; }
  .crm-rule-row { display: flex; justify-content: space-between; font-size: 13px; border-bottom: 1px solid ${C.border}; padding-bottom: 8px; margin-bottom: 8px; }
  .crm-rule-row:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
  .crm-seg-card { border-radius: 12px; padding: 16px 18px; cursor: pointer; transition: all 0.15s; border-width: 1.5px; border-style: solid; }
  .crm-lb-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid ${C.border}; }
  .crm-lb-row:last-child { border-bottom: none; }
  .crm-progress { height: 5px; border-radius: 3px; background: rgba(255,255,255,0.08); overflow: hidden; margin-top: 4px; }
  .crm-progress-bar { height: 100%; border-radius: 3px; transition: width 0.6s; }
  .crm-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; }
  .crm-modal { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 16px; width: 100%; max-width: 480px; max-height: 90vh; overflow: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.5); }
  .crm-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; border-bottom: 1px solid ${C.border}; position: sticky; top: 0; background: ${C.surface}; z-index: 1; }
  .crm-modal-title { margin: 0; font-size: 17px; font-weight: 700; color: ${C.text}; }
  .crm-modal-close { background: none; border: none; font-size: 20px; cursor: pointer; color: ${C.muted}; line-height: 1; }
  .crm-modal-close:hover { color: ${C.text}; }
  .crm-modal-body { padding: 24px; }
  .crm-field { margin-bottom: 16px; }
  .crm-field-label { display: block; font-size: 12px; font-weight: 600; color: ${C.muted}; margin-bottom: 6px; }
  .crm-field-hint { font-size: 10px; color: ${C.muted}; margin-top: 4px; }
  .crm-modal-actions { display: flex; gap: 10px; justify-content: flex-end; }
  .crm-btn-cancel { padding: 10px 20px; background: rgba(255,255,255,0.05); color: ${C.muted}; border: 1px solid ${C.border}; border-radius: 8px; font-weight: 600; cursor: pointer; font-family: inherit; }
  .crm-btn-cancel:hover { color: ${C.text}; }
  .crm-checkbox-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: ${C.sub}; }
  .crm-checkbox-label input[type=checkbox] { accent-color: ${C.teal}; width: 15px; height: 15px; }
  .crm-redeem-preview { background: rgba(255,255,255,0.04); border-radius: 8px; padding: 10px 14px; margin-bottom: 12px; font-size: 12px; color: ${C.gold}; font-weight: 600; border: 1px solid rgba(240,180,41,0.2); }
  .crm-dropdown { position: absolute; top: 100%; left: 0; right: 0; background: ${C.surface}; border: 1.5px solid ${C.border}; border-radius: 8px; z-index: 10; max-height: 150px; overflow-y: auto; box-shadow: 0 8px 24px rgba(0,0,0,0.4); }
  .crm-dropdown-item { padding: 9px 12px; cursor: pointer; font-size: 12px; border-bottom: 1px solid ${C.border}; color: ${C.sub}; }
  .crm-dropdown-item:last-child { border-bottom: none; }
  .crm-dropdown-item:hover { background: rgba(255,255,255,0.04); color: ${C.text}; }
  .crm-sms-grid { display: grid; grid-template-columns: 1fr 360px; gap: 20px; }
  .crm-recipient-bar { display: flex; justify-content: space-between; align-items: center; background: rgba(255,255,255,0.03); border: 1px solid ${C.border}; border-radius: 8px; padding: 10px 14px; margin-bottom: 14px; font-size: 12px; }
  .crm-btn-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
  .crm-template-btn { padding: 9px 14px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; text-align: left; color: ${C.sub}; font-family: inherit; transition: all 0.1s; }
  .crm-template-btn:hover { border-color: ${C.gold}; color: ${C.gold}; background: rgba(240,180,41,0.06); }
  .crm-campaign-card { background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; padding: 12px 14px; margin-bottom: 8px; }
  .crm-campaign-card:last-child { margin-bottom: 0; }
  .crm-birthday-alert { background: rgba(255,107,107,0.1); border: 1.5px solid rgba(255,107,107,0.3); border-radius: 12px; padding: 14px 18px; margin-bottom: 20px; display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .crm-birthday-btn { padding: 8px 16px; background: ${C.coral}; color: white; border: none; border-radius: 8px; font-weight: 700; font-size: 12px; cursor: pointer; font-family: inherit; white-space: nowrap; }
  .crm-birthday-btn:hover { opacity: 0.88; }
  .crm-wish-btn { padding: 5px 14px; background: rgba(240,180,41,0.1); color: ${C.gold}; border: 1.5px solid rgba(240,180,41,0.3); border-radius: 7px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; }
  .crm-wish-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .crm-sms-btn-send { padding: 11px 0; background: ${C.gold}; color: ${C.ink}; border: none; border-radius: 8px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; opacity: 1; transition: opacity 150ms; }
  .crm-sms-btn-send:disabled { opacity: 0.5; cursor: not-allowed; }
  .crm-sms-btn-sched { padding: 11px 0; background: rgba(255,255,255,0.07); color: ${C.sub}; border: 1px solid ${C.border}; border-radius: 8px; font-weight: 600; font-size: 13px; cursor: pointer; font-family: inherit; transition: all 150ms; }
  .crm-sms-btn-sched:disabled { opacity: 0.5; cursor: not-allowed; }
  .crm-sms-btn-sched:not(:disabled):hover { background: rgba(255,255,255,0.1); color: ${C.text}; }
  .crm-var-code { background: rgba(255,255,255,0.06); padding: 0 5px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-size: 11px; color: ${C.sky}; }
  @media (max-width: 768px) { .crm-sms-grid { grid-template-columns: 1fr; } .crm-page { padding: 16px; } }
`;

function SegBadge({ seg }) {
  const m = SEGMENT_META[seg] || SEGMENT_META.new;
  return (
    <span style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}`, fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20 }}>
      {m.icon} {m.label}
    </span>
  );
}

function Modal({ title, onClose, children, maxWidth = 480 }) {
  return (
    <div className="crm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="crm-modal" style={{ maxWidth }}>
        <div className="crm-modal-header">
          <h3 className="crm-modal-title">{title}</h3>
          <button className="crm-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="crm-modal-body">{children}</div>
      </div>
    </div>
  );
}

function KPI({ label, value, color = C.gold, icon }) {
  return (
    <div className="crm-kpi">
      <div className="crm-kpi-icon" style={{ background: color + '18' }}>{icon}</div>
      <div>
        <div className="crm-kpi-val" style={{ color }}>{value}</div>
        <div className="crm-kpi-label">{label}</div>
      </div>
    </div>
  );
}

// ── Tab 1: Overview ───────────────────────────────────────────────────────────
function OverviewTab() {
  const [overview, setOverview]   = useState(null);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch]       = useState('');
  const [segFilter, setSegFilter] = useState('all');
  const [loading, setLoading]     = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, custRes] = await Promise.all([
        fetch(`${API}/crm/overview`, { headers: hdrs() }),
        fetch(`${API}/customers`,    { headers: hdrs() }),
      ]);
      if (ovRes.ok)   setOverview(await ovRes.json());
      if (custRes.ok) setCustomers(await custRes.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.name.toLowerCase().includes(q) || (c.phone || '').includes(q);
    const matchSeg = segFilter === 'all' || c.segment === segFilter;
    return matchSearch && matchSeg;
  });

  return (
    <div>
      <div className="crm-kpi-grid">
        <KPI label="Total Customers"  value={overview?.total       ?? '—'} color={C.sky}     icon="👥" />
        <KPI label="Active 30 Days"   value={overview?.active30    ?? '—'} color={C.teal}    icon="✅" />
        <KPI label="VIP Customers"    value={overview?.vipCount    ?? '—'} color={C.gold}    icon="⭐" />
        <KPI label="At Risk"          value={overview?.atRiskCount ?? '—'} color={C.coral}   icon="⚠️" />
      </div>
      <div className="crm-filter-row">
        <input className="crm-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or phone…" />
        <select className="crm-select" value={segFilter} onChange={(e) => setSegFilter(e.target.value)}>
          <option value="all">All Segments</option>
          {Object.entries(SEGMENT_META).map(([k, m]) => <option key={k} value={k}>{m.icon} {m.label}</option>)}
        </select>
      </div>
      {loading ? <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="crm-table">
            <thead>
              <tr>
                {['Customer', 'Phone', 'Segment', 'Spent', 'Points', 'Last Purchase', 'Birthday', ''].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const daysSince = c.lastPurchaseAt ? Math.floor((Date.now() - new Date(c.lastPurchaseAt)) / 86_400_000) : null;
                const bdDays = c.birthday ? (() => {
                  const b = new Date(c.birthday);
                  const t = new Date();
                  const n = new Date(t.getFullYear(), b.getMonth(), b.getDate());
                  if (n < t) n.setFullYear(t.getFullYear() + 1);
                  return Math.ceil((n - t) / 86_400_000);
                })() : null;
                const initials = c.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
                return (
                  <tr key={c.id}>
                    <td className="crm-td">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(240,180,41,0.15)', color: C.gold, fontWeight: 700, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials}</div>
                        <span style={{ fontWeight: 600 }}>{c.name}</span>
                      </div>
                    </td>
                    <td className="crm-td" style={{ color: C.muted }}>{c.phone || '—'}</td>
                    <td className="crm-td"><SegBadge seg={c.segment} /></td>
                    <td className="crm-td" style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: C.teal }}>{gh(c.totalSpent)}</td>
                    <td className="crm-td" style={{ fontWeight: 700, color: c.loyaltyPoints > 0 ? C.gold : C.muted }}>{c.loyaltyPoints ?? 0}</td>
                    <td className="crm-td" style={{ fontSize: 12, color: daysSince > 60 ? C.coral : C.sub }}>
                      {c.lastPurchaseAt ? new Date(c.lastPurchaseAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short' }) : '—'}
                    </td>
                    <td className="crm-td">
                      {bdDays !== null && bdDays <= 30
                        ? <span style={{ color: bdDays === 0 ? C.coral : C.gold }}>🎂 {bdDays === 0 ? 'Today!' : `${bdDays}d`}</span>
                        : <span style={{ color: C.border }}>—</span>}
                    </td>
                    <td className="crm-td">
                      <a href="/customers" style={{ fontSize: 11, color: C.teal, fontWeight: 600, textDecoration: 'none' }}>View</a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Loyalty Points ─────────────────────────────────────────────────────
function LoyaltyTab() {
  const [settings, setSettings]         = useState(null);
  const [leaderboard, setLeaderboard]   = useState([]);
  const [editModal, setEditModal]       = useState(false);
  const [editForm, setEditForm]         = useState({});
  const [redeemSearch, setRedeemSearch] = useState('');
  const [redeemCustomer, setRedeemCustomer] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState('');
  const [customers, setCustomers]       = useState([]);
  const [saving, setSaving]             = useState(false);
  const [toast, setToast]               = useState('');

  const load = useCallback(async () => {
    const [sr, lr, cr] = await Promise.all([
      fetch(`${API}/crm/loyalty/settings`,    { headers: hdrs() }),
      fetch(`${API}/crm/loyalty/leaderboard`, { headers: hdrs() }),
      fetch(`${API}/customers`,               { headers: hdrs() }),
    ]);
    if (sr.ok) setSettings(await sr.json());
    if (lr.ok) setLeaderboard(await lr.json());
    if (cr.ok) setCustomers(await cr.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/crm/loyalty/settings`, { method: 'PUT', headers: hdrs(), body: JSON.stringify(editForm) });
      if (r.ok) { setSettings(await r.json()); setEditModal(false); setToast('Settings saved'); setTimeout(() => setToast(''), 3000); }
    } finally { setSaving(false); }
  };

  const redeem = async () => {
    if (!redeemCustomer || !redeemPoints) return;
    try {
      const r = await fetch(`${API}/crm/loyalty/redeem`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ customerId: redeemCustomer.id, points: Number(redeemPoints) }),
      });
      const j = await r.json();
      if (r.ok) {
        setToast(`Redeemed ${redeemPoints} points — ${gh(j.discountAmount)} discount`);
        setRedeemPoints(''); setRedeemCustomer(null); setRedeemSearch('');
        await load();
        setTimeout(() => setToast(''), 4000);
      } else {
        setToast(j.error || 'Redemption failed');
        setTimeout(() => setToast(''), 3000);
      }
    } catch { /* ignore */ }
  };

  const matchedCustomers = redeemSearch
    ? customers.filter((c) => c.name.toLowerCase().includes(redeemSearch.toLowerCase()) || (c.phone || '').includes(redeemSearch))
    : [];

  const discountPreview = settings && redeemCustomer && redeemPoints
    ? (Number(redeemPoints) / settings.redeemRate) * Number(settings.redeemValue)
    : 0;

  return (
    <div>
      {toast && <div className="crm-toast">✓ {toast}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div className="crm-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 className="crm-card-title" style={{ margin: 0 }}>Loyalty Rules</h3>
            <button className="crm-btn-outline" onClick={() => { setEditForm({ ...settings }); setEditModal(true); }}>Edit Rules</button>
          </div>
          {settings ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[
                ['Earn Rate',     `Every GHS ${settings.earnRate} = 1 point`],
                ['Redeem Rate',   `${settings.redeemRate} points = ${gh(settings.redeemValue)} discount`],
                ['VIP Threshold', `${settings.vipThreshold} points`],
                ['Status',        settings.isEnabled ? '✅ Enabled' : '❌ Disabled'],
              ].map(([k, v]) => (
                <div key={k} className="crm-rule-row">
                  <span style={{ color: C.muted }}>{k}</span>
                  <span style={{ fontWeight: 600, color: C.gold }}>{v}</span>
                </div>
              ))}
            </div>
          ) : <div style={{ color: C.muted }}>Loading…</div>}
        </div>

        <div className="crm-card">
          <h3 className="crm-card-title">Redeem Points</h3>
          <div style={{ marginBottom: 12, position: 'relative' }}>
            <input className="crm-input" value={redeemSearch} onChange={(e) => { setRedeemSearch(e.target.value); setRedeemCustomer(null); }} placeholder="Search customer by name or phone…" />
            {matchedCustomers.length > 0 && !redeemCustomer && (
              <div className="crm-dropdown">
                {matchedCustomers.map((c) => (
                  <div key={c.id} className="crm-dropdown-item" onClick={() => { setRedeemCustomer(c); setRedeemSearch(c.name); }}>
                    <strong style={{ color: C.text }}>{c.name}</strong> · {c.loyaltyPoints ?? 0} pts
                  </div>
                ))}
              </div>
            )}
          </div>
          {redeemCustomer && (
            <div className="crm-redeem-preview">
              ⭐ {redeemCustomer.name} has {redeemCustomer.loyaltyPoints ?? 0} points
              {discountPreview > 0 && <span style={{ color: C.teal, marginLeft: 8 }}>→ {gh(discountPreview)} discount</span>}
            </div>
          )}
          <input className="crm-input" type="number" min="0" value={redeemPoints} onChange={(e) => setRedeemPoints(e.target.value)} placeholder="Points to redeem" style={{ marginBottom: 12 }} />
          <button className="crm-btn-primary" onClick={redeem} disabled={!redeemCustomer || !redeemPoints} style={{ width: '100%' }}>
            Redeem Points
          </button>
        </div>
      </div>

      <div className="crm-card">
        <h3 className="crm-card-title">🏆 Points Leaderboard</h3>
        {leaderboard.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: C.muted, fontSize: 13 }}>No loyalty points earned yet. Points are earned automatically on each sale.</div>
        ) : leaderboard.map((c, i) => {
          const vipThreshold = settings?.vipThreshold || 500;
          const pct = Math.min((c.loyaltyPoints / vipThreshold) * 100, 100);
          return (
            <div key={c.id} className="crm-lb-row">
              <div style={{ width: 28, fontWeight: 800, fontSize: 13, color: i < 3 ? C.gold : C.muted, textAlign: 'center', flexShrink: 0 }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, color: C.text, fontSize: 13 }}>{c.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <SegBadge seg={c.segment} />
                    <span style={{ fontWeight: 800, color: C.gold, fontSize: 14 }}>{c.loyaltyPoints}</span>
                  </div>
                </div>
                <div className="crm-progress">
                  <div className="crm-progress-bar" style={{ width: `${pct}%`, background: pct >= 100 ? C.gold : C.teal }} />
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                  {pct >= 100 ? '✅ VIP achieved' : `${c.loyaltyPoints}/${vipThreshold} to VIP`}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editModal && settings && (
        <Modal title="Loyalty Settings" onClose={() => setEditModal(false)}>
          {[
            ['earnRate',     'Earn Rate (GHS per 1 point)',        'number', 'e.g. 10 means every GHS10 = 1 point'],
            ['redeemRate',   'Redeem Rate (points for GHS value)', 'number', 'e.g. 100 points = 1 GHS value unit'],
            ['redeemValue',  'Redeem Value (GHS per unit)',        'number', 'e.g. 5 means 100 pts = GHS5 discount'],
            ['vipThreshold', 'VIP Threshold (points)',             'number', 'Points needed to reach VIP status'],
          ].map(([k, label, type, hint]) => (
            <div key={k} className="crm-field">
              <label className="crm-field-label">{label}</label>
              <input className="crm-input" type={type} value={editForm[k] ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, [k]: e.target.value }))} />
              <div className="crm-field-hint">{hint}</div>
            </div>
          ))}
          <div style={{ marginBottom: 20 }}>
            <label className="crm-checkbox-label">
              <input type="checkbox" checked={editForm.isEnabled ?? true} onChange={(e) => setEditForm((f) => ({ ...f, isEnabled: e.target.checked }))} />
              Enable loyalty programme
            </label>
          </div>
          <div className="crm-modal-actions">
            <button className="crm-btn-cancel" onClick={() => setEditModal(false)}>Cancel</button>
            <button className="crm-btn-primary" onClick={saveSettings} disabled={saving}>{saving ? 'Saving…' : 'Save Settings'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab 3: Segments ───────────────────────────────────────────────────────────
function SegmentsTab({ onSMSSegment }) {
  const [data, setData]       = useState(null);
  const [active, setActive]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalc, setRecalc]   = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/crm/segments`, { headers: hdrs() })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setData(d); })
      .finally(() => setLoading(false));
  }, [recalc]);

  const doRecalc = async () => {
    await fetch(`${API}/crm/segments/recalculate`, { method: 'POST', headers: hdrs() });
    setRecalc((r) => !r);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Loading…</div>;
  const counts    = data?.counts || {};
  const customers = active ? (data?.customers?.[active] || []) : Object.values(data?.customers || {}).flat();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="crm-btn-outline" onClick={doRecalc}>🔄 Recalculate Segments</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {Object.entries(SEGMENT_META).map(([key, m]) => (
          <div
            key={key}
            onClick={() => setActive(active === key ? null : key)}
            className="crm-seg-card"
            style={{
              background: active === key ? m.color : m.bg,
              borderColor: m.border,
            }}
          >
            <div style={{ fontSize: 22, marginBottom: 6 }}>{m.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: active === key ? C.ink : m.color, lineHeight: 1 }}>{counts[key] ?? 0}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: active === key ? C.ink : m.color, marginTop: 4 }}>{m.label}</div>
            <div style={{ fontSize: 11, color: active === key ? 'rgba(11,14,23,0.6)' : C.muted, marginTop: 4, lineHeight: 1.4 }}>{m.desc}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: C.muted }}>
          {active ? `${SEGMENT_META[active]?.label} customers` : 'All customers'} — {customers.length}
        </div>
        {active && (
          <button className="crm-btn-primary" onClick={() => onSMSSegment(active)}>📱 SMS This Segment</button>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="crm-table">
          <thead>
            <tr>
              {['Customer', 'Phone', 'Segment', 'Points', 'Total Spent', 'Last Purchase'].map((h) => <th key={h}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id}>
                <td className="crm-td" style={{ fontWeight: 600 }}>{c.name}</td>
                <td className="crm-td" style={{ color: C.muted }}>{c.phone || '—'}</td>
                <td className="crm-td"><SegBadge seg={c.segment} /></td>
                <td className="crm-td" style={{ fontWeight: 700, color: C.gold }}>{c.loyaltyPoints ?? 0}</td>
                <td className="crm-td" style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: C.teal }}>{gh(c.totalSpent)}</td>
                <td className="crm-td" style={{ fontSize: 12, color: C.sub }}>
                  {c.lastPurchaseAt ? new Date(c.lastPurchaseAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab 4: Bulk SMS ───────────────────────────────────────────────────────────
const SMS_TEMPLATES = [
  { name: '🔄 Win-Back',      segment: 'atRisk', msg: "Hi {name}, we miss you! It's been a while since your last visit. Come back today and enjoy a special discount just for you. 😊" },
  { name: '⭐ VIP Exclusive', segment: 'vip',    msg: 'Hi {name}, as one of our VIP customers, we have an exclusive offer just for you! Visit us this week for a special surprise. 🌟' },
  { name: '📦 Restock Alert', segment: 'all',    msg: "Hi {name}! Great news — we've just restocked our shelves with fresh products. Come in and check out what's new. 🛍️" },
  { name: '🎂 Birthday Wish', segment: 'all',    msg: 'Happy Birthday {name}! 🎂 Enjoy 10% off your next purchase today. Valid today only. Show this message in store. 🎉' },
];

function SMSTab({ defaultSegment, onSegmentHandled }) {
  const [segment, setSegment]         = useState(defaultSegment || 'all');
  const [message, setMessage]         = useState('');
  const [name, setName]               = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [sending, setSending]         = useState(false);
  const [campaigns, setCampaigns]     = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [customers, setCustomers]     = useState([]);
  const [toast, setToast]             = useState('');

  useEffect(() => {
    if (defaultSegment) { setSegment(defaultSegment); onSegmentHandled?.(); }
  }, [defaultSegment]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/crm/sms/campaigns`, { headers: hdrs() }).then((r) => r.ok ? r.json() : []),
      fetch(`${API}/customers`,         { headers: hdrs() }).then((r) => r.ok ? r.json() : []),
    ]).then(([camps, custs]) => { setCampaigns(camps); setCustomers(custs); });
  }, []);

  useEffect(() => {
    setRecipientCount(segment === 'all' ? customers.length : customers.filter((c) => c.segment === segment).length);
  }, [segment, customers]);

  const send = async (schedule = false) => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const endpoint = schedule ? `${API}/crm/sms/schedule` : `${API}/crm/sms/send`;
      const body = { segment, message, name: name || `Campaign ${new Date().toLocaleDateString()}` };
      if (schedule && scheduledAt) body.scheduledAt = scheduledAt;
      const r = await fetch(endpoint, { method: 'POST', headers: hdrs(), body: JSON.stringify(body) });
      const j = await r.json();
      if (schedule) {
        setToast(`Scheduled for ${new Date(scheduledAt).toLocaleString()}`);
      } else {
        const parts = [];
        if (j.smsSent   > 0) parts.push(`${j.smsSent} via SMS`);
        if (j.emailSent > 0) parts.push(`${j.emailSent} via email`);
        if (j.failed    > 0) parts.push(`${j.failed} failed`);
        setToast(parts.length ? parts.join(' · ') : `Sent to ${j.sent} recipient(s)`);
      }
      setTimeout(() => setToast(''), 4000);
      const cr = await fetch(`${API}/crm/sms/campaigns`, { headers: hdrs() });
      if (cr.ok) setCampaigns(await cr.json());
    } finally { setSending(false); }
  };

  const estCost = (recipientCount * 0.05).toFixed(2);

  return (
    <div className="crm-sms-grid">
      <div>
        {toast && <div className="crm-toast">✓ {toast}</div>}

        <div className="crm-field">
          <label className="crm-field-label">Campaign Name</label>
          <input className="crm-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. July Win-Back Campaign" />
        </div>

        <div className="crm-field">
          <label className="crm-field-label">Send To</label>
          <select className="crm-select" value={segment} onChange={(e) => setSegment(e.target.value)} style={{ width: '100%' }}>
            <option value="all">All Customers ({customers.length})</option>
            {Object.entries(SEGMENT_META).map(([k, m]) => (
              <option key={k} value={k}>{m.icon} {m.label} ({customers.filter((c) => c.segment === k).length})</option>
            ))}
          </select>
        </div>

        <div className="crm-field">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label className="crm-field-label" style={{ margin: 0 }}>Message</label>
            <span style={{ fontSize: 11, color: message.length > 160 ? C.coral : C.muted }}>{message.length}/160 chars</span>
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message… Use {name} {points} {lastpurchase}"
            rows={5}
            style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${message.length > 160 ? C.coral : C.border}`, borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box', color: C.text }}
          />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            Variables: <span className="crm-var-code">{'{name}'}</span>{' '}
            <span className="crm-var-code">{'{points}'}</span>{' '}
            <span className="crm-var-code">{'{lastpurchase}'}</span>
          </div>
        </div>

        <div className="crm-recipient-bar">
          <span style={{ color: C.muted }}>Recipients: <strong style={{ color: C.sky }}>{recipientCount}</strong></span>
          <span style={{ color: C.muted }}>Est. cost: <strong style={{ color: C.gold }}>GHS {estCost}</strong></span>
        </div>

        <div className="crm-btn-grid">
          <button className="crm-sms-btn-send" onClick={() => send(false)} disabled={sending || !message.trim()}>
            {sending ? 'Sending…' : '📤 Send Now'}
          </button>
          <button className="crm-sms-btn-sched" onClick={() => send(true)} disabled={sending || !message.trim() || !scheduledAt}>
            🕐 Schedule
          </button>
        </div>

        <div className="crm-field">
          <label className="crm-field-label">Schedule Date/Time</label>
          <input className="crm-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 8 }}>Quick Templates</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {SMS_TEMPLATES.map((t) => (
              <button key={t.name} className="crm-template-btn" onClick={() => { setMessage(t.msg); setSegment(t.segment); }}>
                {t.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Campaign History</div>
        {campaigns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: C.muted, fontSize: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: `1.5px dashed ${C.border}` }}>
            No campaigns yet
          </div>
        ) : (
          <div>
            {campaigns.map((c) => {
              const sm = { sent: { bg: 'rgba(0,201,177,0.1)', color: C.teal }, scheduled: { bg: 'rgba(240,180,41,0.1)', color: C.gold }, draft: { bg: 'rgba(255,255,255,0.05)', color: C.muted } }[c.status] || { bg: 'rgba(255,255,255,0.05)', color: C.muted };
              return (
                <div key={c.id} className="crm-campaign-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: C.text }}>{c.name}</div>
                    <span style={{ background: sm.bg, color: sm.color, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>{c.status}</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {c.recipientCount} recipients · {(c.segment === 'all' ? 'All' : SEGMENT_META[c.segment]?.label) || c.segment}
                  </div>
                  {c.status === 'sent' && (c.smsSent > 0 || c.emailSent > 0 || c.failed > 0) && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                      {c.smsSent > 0 && <span style={{ fontSize: 10, fontWeight: 600, background: 'rgba(0,201,177,0.1)', color: C.teal, padding: '1px 6px', borderRadius: 8 }}>📱 {c.smsSent} SMS</span>}
                      {c.emailSent > 0 && <span style={{ fontSize: 10, fontWeight: 600, background: 'rgba(56,189,248,0.1)', color: C.sky, padding: '1px 6px', borderRadius: 8 }}>✉ {c.emailSent} email</span>}
                      {c.failed > 0 && <span style={{ fontSize: 10, fontWeight: 600, background: 'rgba(255,107,107,0.1)', color: C.coral, padding: '1px 6px', borderRadius: 8 }}>✗ {c.failed} failed</span>}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                    {c.sentAt ? `Sent ${new Date(c.sentAt).toLocaleDateString()}` : c.scheduledAt ? `Scheduled ${new Date(c.scheduledAt).toLocaleDateString()}` : new Date(c.createdAt).toLocaleDateString()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab 5: Birthdays ──────────────────────────────────────────────────────────
function BirthdaysTab() {
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [sending, setSending]     = useState({});
  const [toast, setToast]         = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/crm/birthdays`, { headers: hdrs() });
      if (r.ok) setBirthdays(await r.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendWish = async (id, name) => {
    setSending((s) => ({ ...s, [id]: true }));
    try {
      const r = await fetch(`${API}/crm/birthdays/send/${id}`, { method: 'POST', headers: hdrs() });
      const j = await r.json();
      setToast(j.message || `Birthday wish sent to ${name}`);
      setTimeout(() => setToast(''), 4000);
    } finally { setSending((s) => ({ ...s, [id]: false })); }
  };

  const sendAllToday = async () => {
    await fetch(`${API}/crm/birthdays/send-all`, { method: 'POST', headers: hdrs() });
    setToast('Birthday wishes sent to all customers with birthdays today!');
    setTimeout(() => setToast(''), 4000);
  };

  const today    = birthdays.filter((c) => c.daysAway === 0);
  const upcoming = birthdays.filter((c) => c.daysAway > 0);

  return (
    <div>
      {toast && <div className="crm-toast">✓ {toast}</div>}

      {today.length > 0 && (
        <div className="crm-birthday-alert">
          <span style={{ fontSize: 24 }}>🎂</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.coral }}>
              {today.length} customer{today.length > 1 ? 's have' : ' has'} a birthday today!
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{today.map((c) => c.name).join(', ')}</div>
          </div>
          <button className="crm-birthday-btn" onClick={sendAllToday}>Send All Wishes</button>
        </div>
      )}

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Loading…</div>
        : birthdays.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: `1.5px dashed ${C.border}` }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🎂</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.sub, marginBottom: 8 }}>No upcoming birthdays</div>
            <div style={{ fontSize: 12, color: C.muted }}>Add birthdays when editing customer profiles to get reminders here.</div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>Upcoming Birthdays (next 30 days)</div>
            <div style={{ overflowX: 'auto' }}>
              <table className="crm-table">
                <thead>
                  <tr>
                    {['Customer', 'Birthday', 'Days Away', 'Segment', 'Total Spent', 'Action'].map((h) => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {birthdays.map((c) => {
                    const daysColor = c.daysAway === 0 ? C.coral : c.daysAway <= 7 ? C.gold : C.sky;
                    const daysBg    = c.daysAway === 0 ? 'rgba(255,107,107,0.12)' : c.daysAway <= 7 ? 'rgba(240,180,41,0.12)' : 'rgba(56,189,248,0.1)';
                    const daysBorder = c.daysAway === 0 ? 'rgba(255,107,107,0.3)' : c.daysAway <= 7 ? 'rgba(240,180,41,0.3)' : 'rgba(56,189,248,0.25)';
                    return (
                      <tr key={c.id}>
                        <td className="crm-td" style={{ fontWeight: 600 }}>{c.name}</td>
                        <td className="crm-td" style={{ color: C.sub }}>{new Date(c.birthday).toLocaleDateString('en-GH', { day: 'numeric', month: 'long' })}</td>
                        <td className="crm-td">
                          <span style={{ background: daysBg, color: daysColor, border: `1px solid ${daysBorder}`, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
                            {c.daysAway === 0 ? 'Today! 🎂' : `${c.daysAway} days`}
                          </span>
                        </td>
                        <td className="crm-td"><SegBadge seg={c.segment} /></td>
                        <td className="crm-td" style={{ fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: C.teal }}>{gh(c.totalSpent)}</td>
                        <td className="crm-td">
                          <button className="crm-wish-btn" onClick={() => sendWish(c.id, c.name)} disabled={sending[c.id]}>
                            {sending[c.id] ? '…' : '🎂 Send Wish'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
}

// ── Main CRM Page ─────────────────────────────────────────────────────────────
export default function CRM() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tabIdx = TAB_KEYS.indexOf(tabParam) >= 0 ? TAB_KEYS.indexOf(tabParam) : 0;
  const [tab, setTab] = useState(tabIdx);
  const [smsSegment, setSmsSegment] = useState(null);

  const switchTab = (i) => { setTab(i); setSearchParams({ tab: TAB_KEYS[i] }); };

  useEffect(() => {
    const idx = TAB_KEYS.indexOf(searchParams.get('tab'));
    if (idx >= 0 && idx !== tab) setTab(idx);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSMSSegment = (seg) => {
    setSmsSegment(seg);
    switchTab(3);
  };

  return (
    <Layout>
      <style>{STYLES}</style>
      <div className="crm-page">
        <h1 className="crm-heading">CRM</h1>
        <p className="crm-sub">Loyalty points, customer segments, bulk SMS, and birthday reminders</p>

        <div className="crm-tabs">
          {TAB_LABELS.map((label, i) => (
            <button key={label} onClick={() => switchTab(i)} className={`crm-tab${tab === i ? ' active' : ''}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="crm-panel">
          {tab === 0 && <OverviewTab />}
          {tab === 1 && <LoyaltyTab />}
          {tab === 2 && <SegmentsTab onSMSSegment={handleSMSSegment} />}
          {tab === 3 && <SMSTab defaultSegment={smsSegment} onSegmentHandled={() => setSmsSegment(null)} />}
          {tab === 4 && <BirthdaysTab />}
        </div>
      </div>
    </Layout>
  );
}
